import { useEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Ear, Mic, X, Sparkles, ArrowUpRight, Volume2 } from "lucide-react";
import { api } from "@/lib/api";
import { VoiceWave } from "@/components/Bubbles";
import { buildWakeMatcher, getVoicePref } from "@/lib/voice";
import { playUrl, stopAudio, ttsUrl, speakFallback, FATAL_SR_ERRORS } from "@/lib/audio";
import { toast } from "sonner";

export function WakeListener() {
  const [on, setOn] = useState(() => localStorage.getItem("omy_wake") === "1");
  const [bubble, setBubble] = useState(false);
  const onRef = useRef(on);
  const recRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();
  const onAssistant = location.pathname.startsWith("/app/assistant");

  useEffect(() => { onRef.current = on; localStorage.setItem("omy_wake", on ? "1" : "0"); }, [on]);

  useEffect(() => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!on || onAssistant || bubble || !SR) {
      if (recRef.current) { try { recRef.current.stop(); } catch (e) {} recRef.current = null; }
      return;
    }
    const matcher = buildWakeMatcher();
    const rec = new SR();
    rec.lang = "fr-FR"; rec.continuous = true; rec.interimResults = true;
    recRef.current = rec;
    rec.onresult = (e) => {
      let t = ""; for (let i = 0; i < e.results.length; i++) t += e.results[i][0].transcript;
      if (matcher(t)) {
        try { rec.stop(); } catch (er) {}
        recRef.current = null;
        setBubble(true);
      }
    };
    let dead = false;
    rec.onerror = (e) => {
      if (FATAL_SR_ERRORS.includes(e.error)) { dead = true; setOn(false); toast.error("Micro inaccessible : autorise le micro pour activer « Hey Omy »."); }
    };
    rec.onend = () => {
      if (dead || recRef.current !== rec) return;
      setTimeout(() => { if (!dead && recRef.current === rec && onRef.current) { try { rec.start(); } catch (er) {} } }, 300);
    };
    try { rec.start(); } catch (er) {}
    return () => { dead = true; if (recRef.current === rec) recRef.current = null; try { rec.stop(); } catch (e) {} };
  }, [on, bubble, onAssistant, location.pathname]);

  const SR = typeof window !== "undefined" && (window.SpeechRecognition || window.webkitSpeechRecognition);
  if (!SR) return null;

  return (
    <>
      {bubble && <VoiceBubble onClose={() => setBubble(false)} onOpenAssistant={() => { setBubble(false); navigate("/app/assistant"); }} />}
      {!onAssistant && (
        <div className="fixed bottom-5 right-5 z-40 flex items-center gap-2">
          <button data-testid="wake-global-toggle" onClick={() => setOn(!on)}
            title={on ? "« Hey Omy » activé — dis-le depuis n'importe où" : "Activer l'écoute « Hey Omy »"}
            className={`w-11 h-11 rounded-full flex items-center justify-center shadow-lg transition-all ${on ? "pulse-ring" : "hover:scale-105"}`}
            style={{ background: on ? "linear-gradient(135deg,#E05A36,#F59E0B)" : "hsl(222 14% 14%)", border: "1px solid rgba(255,255,255,0.1)" }}>
            <Ear size={18} className={on ? "text-white" : "text-muted-foreground"} />
          </button>
          <button data-testid="omy-launcher" onClick={() => setBubble(true)}
            className="inline-flex items-center gap-2 rounded-full pl-4 pr-5 h-12 shadow-xl text-white font-medium hover:scale-[1.03] transition-transform"
            style={{ background: "linear-gradient(135deg,#E05A36,#F59E0B)", border: "1px solid rgba(255,255,255,0.12)" }}>
            <Sparkles size={18} /> Demander à OMY
          </button>
        </div>
      )}
    </>
  );
}

function VoiceBubble({ onClose, onOpenAssistant }) {
  const [phase, setPhase] = useState("listening"); // listening | thinking | answered
  const [question, setQuestion] = useState("");
  const [reply, setReply] = useState("");
  const [speaking, setSpeaking] = useState(false);
  const recRef = useRef(null);

  const stripEmoji = (t) => t.replace(/[\u{1F000}-\u{1FFFF}\u{2600}-\u{27BF}\u{FE0F}\u{2B00}-\u{2BFF}👇]/gu, "").trim();

  const speak = async (text) => {
    const clean = stripEmoji(text);
    if (!clean) return;
    setSpeaking(true);
    try {
      await playUrl(ttsUrl(clean, getVoicePref()));
      setSpeaking(false);
    } catch (e) {
      setSpeaking(false);
      speakFallback(clean, { onstart: () => setSpeaking(true), onend: () => setSpeaking(false) });
    }
  };

  const ask = async (msg) => {
    setPhase("thinking");
    try {
      const { data } = await api.post("/assistant/chat", { message: msg });
      setReply(data.reply);
      setPhase("answered");
      speak(data.reply);
    } catch (e) {
      setReply("OMY est momentanément indisponible. Réessaie.");
      setPhase("answered");
    }
  };

  const listen = () => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) { setPhase("answered"); setReply("La reconnaissance vocale n'est pas supportée par ce navigateur."); return; }
    stopAudio(); setSpeaking(false);
    setQuestion(""); setReply(""); setPhase("listening");
    const rec = new SR();
    rec.lang = "fr-FR"; rec.interimResults = true; rec.continuous = false;
    recRef.current = rec;
    let finalText = "";
    rec.onresult = (e) => { let t = ""; for (let i = 0; i < e.results.length; i++) t += e.results[i][0].transcript; finalText = t; setQuestion(t); };
    rec.onerror = (e) => { if (FATAL_SR_ERRORS.includes(e.error)) setReply("Micro inaccessible : autorise le micro pour ce site dans les réglages du navigateur."); };
    rec.onend = () => { if (finalText.trim()) ask(finalText.trim()); else setPhase("answered"); };
    try { rec.start(); } catch (e) {}
  };

  useEffect(() => {
    const t = setTimeout(() => listen(), 250);
    return () => {
      clearTimeout(t);
      if (recRef.current) { try { recRef.current.stop(); } catch (e) {} }
      stopAudio();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div data-testid="voice-bubble" className="fixed bottom-24 right-4 sm:right-5 z-50 w-[calc(100vw-2rem)] max-w-sm animate-fadeUp">
      <div className="omy-glass omy-glow rounded-3xl p-4 shadow-2xl">
        <div className="flex items-center gap-2 mb-3">
          <div className="w-8 h-8 rounded-full flex items-center justify-center shrink-0" style={{ background: "linear-gradient(135deg,#E05A36,#F59E0B)" }}>
            <Sparkles size={15} className="text-white" />
          </div>
          <span className="text-sm font-display font-semibold flex-1">OMY</span>
          <button data-testid="voice-bubble-open-assistant" onClick={onOpenAssistant} title="Ouvrir l'assistant complet"
            className="w-8 h-8 rounded-full flex items-center justify-center text-muted-foreground hover:text-primary hover:bg-white/5">
            <ArrowUpRight size={17} />
          </button>
          <button data-testid="voice-bubble-close" onClick={onClose} title="Fermer"
            className="w-8 h-8 rounded-full flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-white/5">
            <X size={17} />
          </button>
        </div>

        {phase === "listening" && (
          <div data-testid="voice-bubble-listening" className="flex items-center gap-3 rounded-2xl bg-red-500/10 border border-red-500/20 px-4 py-3">
            <span className="w-2.5 h-2.5 rounded-full bg-red-500 animate-pulse shrink-0" />
            <span className="text-sm text-foreground/90 flex-1">{question || "OMY écoute…"}</span>
            <VoiceWave active bars={6} />
          </div>
        )}

        {phase === "thinking" && (
          <div className="space-y-2">
            {question && <div className="text-sm text-right text-foreground/70">« {question} »</div>}
            <div className="flex items-center gap-2 rounded-2xl bg-black/30 px-4 py-3"><VoiceWave bars={4} /><span className="text-sm text-muted-foreground">OMY réfléchit…</span></div>
          </div>
        )}

        {phase === "answered" && (
          <div className="space-y-2">
            {question && <div data-testid="voice-bubble-question" className="text-sm text-right text-foreground/70">« {question} »</div>}
            <div data-testid="voice-bubble-reply" className="flex items-start gap-2.5 rounded-2xl bg-black/30 px-4 py-3 text-sm text-foreground/90">
              <button onClick={() => speak(reply)} aria-label="Réécouter" className="mt-0.5 text-primary shrink-0 hover:scale-110 transition-transform">
                {speaking ? <VoiceWave active bars={4} /> : <Volume2 size={16} />}
              </button>
              <span className="whitespace-pre-wrap leading-relaxed">{reply}</span>
            </div>
          </div>
        )}

        <div className="flex items-center gap-2 mt-3">
          <button data-testid="voice-bubble-mic" onClick={listen}
            className="flex-1 h-10 rounded-full flex items-center justify-center gap-2 text-sm font-medium text-white transition-all"
            style={{ background: phase === "listening" ? "linear-gradient(135deg,#EF4444,#F97316)" : "linear-gradient(135deg,#E05A36,#F59E0B)" }}>
            <Mic size={16} /> {phase === "listening" ? "J'écoute…" : "Reposer une question"}
          </button>
        </div>
      </div>
    </div>
  );
}
